import {
  Button,
  Group,
  Modal,
  Paper,
  Radio,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import {
  ObjectDeviationMetadataType,
  ObjectDeviationType,
  UpsertObjectDeviationRequest,
} from "../../../../types/ObjectDeviation";
import styles from "./AddEditDeviationModal.module.css";

interface AddEditDeviationModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (request: UpsertObjectDeviationRequest) => Promise<void>;
  deviation: ObjectDeviationType | null;
  metadata: ObjectDeviationMetadataType;
  objectId: number;
  contractId: number;
  addressSystemId: number;
  getTranslation: (key: string) => string | null;
}

interface DeviationFormValues {
  deviationDate: Date | null;
  deviationNumber: string;
  deviationType: string;
  deviationTarget: string;
  deviationTargetDescription: string;
  deviationSource: string;
  deviationSourceDescription: string;
  phaseDescription: string;
  description: string;
  effectToQuality: string;
  effectToValue: string;
  deviationAction: string;
  costs: string;
  fixPlan: string;
  preventPlan: string;
  deviationAccept: string;
  supervisorComments: string;
}

const emptyValues = (): DeviationFormValues => ({
  deviationDate: new Date(),
  deviationNumber: "",
  deviationType: "",
  deviationTarget: "",
  deviationTargetDescription: "",
  deviationSource: "",
  deviationSourceDescription: "",
  phaseDescription: "",
  description: "",
  effectToQuality: "",
  effectToValue: "",
  deviationAction: "",
  costs: "",
  fixPlan: "",
  preventPlan: "",
  deviationAccept: "",
  supervisorComments: "",
});

const toLocalDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function AddEditDeviationModal({
  opened,
  onClose,
  onSave,
  deviation,
  metadata,
  objectId,
  contractId,
  addressSystemId,
  getTranslation,
}: Readonly<AddEditDeviationModalProps>) {
  const required = getTranslation("DEVIATION_VALIDATION_REQUIRED") || "Required";
  const [submitting, setSubmitting] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const form = useForm<DeviationFormValues>({
    initialValues: emptyValues(),
    validate: {
      deviationDate: (value) => (value ? null : required),
      deviationType: (value) => (value ? null : required),
      deviationTarget: (value) => (value ? null : required),
      deviationTargetDescription: (value, values) =>
        Number(values.deviationTarget) === metadata.otherTargetId &&
        !value.trim()
          ? required
          : null,
      deviationSource: (value) => (value ? null : required),
      effectToQuality: (value) => (value ? null : required),
      effectToValue: (value) => (value ? null : required),
      deviationAction: (value) => (value ? null : required),
      deviationAccept: (value) => (value ? null : required),
    },
  });

  useEffect(() => {
    if (!opened) return;
    setValidationMessage("");
    if (!deviation) {
      form.setValues(emptyValues());
      form.resetDirty();
      return;
    }

    form.setValues({
      deviationDate: deviation.deviationDate
        ? new Date(deviation.deviationDate)
        : new Date(),
      deviationNumber: deviation.deviationNumber ?? "",
      deviationType: String(deviation.deviationType),
      deviationTarget: String(deviation.deviationTarget),
      deviationTargetDescription:
        deviation.deviationTargetDescription ?? "",
      deviationSource: String(deviation.deviationSource),
      deviationSourceDescription:
        deviation.deviationSourceDescription ?? "",
      phaseDescription: deviation.phaseDescription ?? "",
      description: deviation.description ?? "",
      effectToQuality: String(deviation.effectToQuality),
      effectToValue: String(deviation.effectToValue),
      deviationAction: String(deviation.deviationAction),
      costs: deviation.costs ?? "",
      fixPlan: deviation.fixPlan ?? "",
      preventPlan: deviation.preventPlan ?? "",
      deviationAccept: String(deviation.deviationAccept),
      supervisorComments: deviation.supervisorComments ?? "",
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, deviation?.id]);

  const submit = form.onSubmit(
    async (values) => {
      setValidationMessage("");
      setSubmitting(true);
      try {
        await onSave({
          objectId,
          contractId,
          addressSystemId,
          deviationDate: toLocalDateString(values.deviationDate!),
          deviationNumber: values.deviationNumber,
          deviationType: Number(values.deviationType),
          deviationTarget: Number(values.deviationTarget),
          deviationTargetDescription: values.deviationTargetDescription,
          deviationSource: Number(values.deviationSource),
          deviationSourceDescription: values.deviationSourceDescription,
          phaseDescription: values.phaseDescription,
          description: values.description,
          effectToQuality: Number(values.effectToQuality),
          effectToValue: Number(values.effectToValue),
          deviationAction: Number(values.deviationAction),
          costs: values.costs,
          fixPlan: values.fixPlan,
          preventPlan: values.preventPlan,
          deviationAccept: Number(values.deviationAccept),
          supervisorComments: values.supervisorComments,
        });
      } catch {
        // The parent displays the API error notification.
      } finally {
        setSubmitting(false);
      }
    },
    () => {
      setValidationMessage(
        getTranslation("DEVIATION_MESSAGE_REQUIRED_FIELDS") ||
          "Complete all required fields before saving.",
      );
    },
  );

  const sectionTitle = (number: number, text: string) => (
    <Title order={5}>
      {number}. {text}
    </Title>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        deviation
          ? getTranslation("DEVIATION_TITLE_EDIT") || "Edit deviation"
          : getTranslation("DEVIATION_TITLE_ADD") || "Add deviation"
      }
      size="xl"
      centered
      classNames={{ body: styles.body }}
    >
      <form onSubmit={submit}>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <DatePickerInput
              label={getTranslation("DEVIATION_LABEL_DATE") || "Date"}
              valueFormat="DD.MM.YYYY"
              required
              {...form.getInputProps("deviationDate")}
            />
            <TextInput
              label={
                getTranslation("DEVIATION_LABEL_REPORT_NUMBER") ||
                "Report number"
              }
              {...form.getInputProps("deviationNumber")}
            />
          </SimpleGrid>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                1,
                getTranslation("DEVIATION_SECTION_TYPE") || "Deviation type",
              )}
              <Radio.Group {...form.getInputProps("deviationType")} required>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {metadata.types.map((option) => (
                    <Radio
                      key={option.id}
                      value={String(option.id)}
                      label={option.label}
                    />
                  ))}
                </SimpleGrid>
              </Radio.Group>
              <Radio.Group {...form.getInputProps("deviationTarget")} required>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {metadata.targets.map((option) => (
                    <Radio
                      key={option.id}
                      value={String(option.id)}
                      label={option.label}
                    />
                  ))}
                </SimpleGrid>
              </Radio.Group>
              {Number(form.values.deviationTarget) ===
                metadata.otherTargetId && (
                <TextInput
                  label={
                    getTranslation("DEVIATION_LABEL_OTHER_TARGET") ||
                    "Other target"
                  }
                  required
                  {...form.getInputProps("deviationTargetDescription")}
                />
              )}
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                2,
                getTranslation("DEVIATION_SECTION_OBSERVER") ||
                  "Observation made by",
              )}
              <Radio.Group {...form.getInputProps("deviationSource")} required>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Radio
                    value="1"
                    label={
                      getTranslation("DEVIATION_SOURCE_OWN") ||
                      "Own observation"
                    }
                  />
                  <Radio
                    value="2"
                    label={
                      getTranslation("DEVIATION_SOURCE_CLIENT") ||
                      "Client observation"
                    }
                  />
                  <Radio
                    value="3"
                    label={
                      getTranslation("DEVIATION_SOURCE_OTHER") ||
                      "Other, specify"
                    }
                  />
                </SimpleGrid>
              </Radio.Group>
              {form.values.deviationSource === "3" && (
                <TextInput
                  label={
                    getTranslation("DEVIATION_LABEL_OTHER_OBSERVER") ||
                    "Other observer"
                  }
                  {...form.getInputProps("deviationSourceDescription")}
                />
              )}
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                3,
                getTranslation("DEVIATION_SECTION_DESCRIPTION") ||
                  "Deviation description",
              )}
              <Textarea
                label={
                  getTranslation("DEVIATION_LABEL_RELATED_ENTRY") ||
                  "Entry related to the deviation"
                }
                minRows={2}
                {...form.getInputProps("phaseDescription")}
              />
              <Textarea
                label={
                  getTranslation("DEVIATION_LABEL_DESCRIPTION_LOCATION") ||
                  "Description and location of the deviation"
                }
                minRows={3}
                {...form.getInputProps("description")}
              />
              <Text fw={500}>
                {getTranslation("DEVIATION_LABEL_EFFECT") ||
                  "Effect of the deviation"}
              </Text>
              <Radio.Group
                {...form.getInputProps("effectToQuality")}
                required
              >
                <Group>
                  <Radio
                    value="1"
                    label={
                      getTranslation("DEVIATION_EFFECTS_QUALITY") ||
                      "Affects quality"
                    }
                  />
                  <Radio
                    value="2"
                    label={
                      getTranslation("DEVIATION_NO_QUALITY_EFFECT") ||
                      "Does not affect quality"
                    }
                  />
                </Group>
              </Radio.Group>
              <Radio.Group
                {...form.getInputProps("effectToValue")}
                required
              >
                <Group>
                  <Radio
                    value="1"
                    label={
                      getTranslation("DEVIATION_EFFECTS_VALUE") ||
                      "Affects reductions in value"
                    }
                  />
                  <Radio
                    value="2"
                    label={
                      getTranslation("DEVIATION_NO_VALUE_EFFECT") ||
                      "Does not affect reductions in value"
                    }
                  />
                </Group>
              </Radio.Group>
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                4,
                getTranslation("DEVIATION_SECTION_ACTIONS") ||
                  "Actions caused by the deviation",
              )}
              <Radio.Group {...form.getInputProps("deviationAction")} required>
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Radio
                    value="1"
                    label={
                      getTranslation("DEVIATION_ACTION_REJECT_REDO") ||
                      "Reject / redo"
                    }
                  />
                  <Radio
                    value="2"
                    label={
                      getTranslation("DEVIATION_ACTION_RETURN_CLAIM") ||
                      "Return / make a claim"
                    }
                  />
                  <Radio
                    value="3"
                    label={
                      getTranslation("DEVIATION_ACTION_REPAIR") || "Repair"
                    }
                  />
                  <Radio
                    value="4"
                    label={
                      getTranslation("DEVIATION_ACTION_USE_AS_IS") ||
                      "Use as is"
                    }
                  />
                </SimpleGrid>
              </Radio.Group>
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                5,
                getTranslation("DEVIATION_SECTION_COSTS") ||
                  "Estimated costs caused by the deviation",
              )}
              <Textarea minRows={3} {...form.getInputProps("costs")} />
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                6,
                getTranslation("DEVIATION_SECTION_REPAIR") ||
                  "Deviation repair (repair plan when needed)",
              )}
              <Textarea minRows={3} {...form.getInputProps("fixPlan")} />
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                7,
                getTranslation("DEVIATION_SECTION_PREVENTION") ||
                  "How a similar deviation can be prevented",
              )}
              <Textarea minRows={3} {...form.getInputProps("preventPlan")} />
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                8,
                getTranslation("DEVIATION_SECTION_ACCEPTANCE") ||
                  "Acceptance of actions (supervisor signature)",
              )}
              <Radio.Group {...form.getInputProps("deviationAccept")} required>
                <Stack gap="xs">
                  <Radio
                    value="1"
                    label={
                      getTranslation("DEVIATION_ACCEPT_USE_AS_IS") ||
                      "Client accepts use as is"
                    }
                  />
                  <Radio
                    value="2"
                    label={
                      getTranslation("DEVIATION_ACCEPT_REPAIR") ||
                      "Client accepts the proposed repair"
                    }
                  />
                  <Radio
                    value="3"
                    label={
                      getTranslation("DEVIATION_REJECT_REPAIR") ||
                      "Client rejects the proposed repair"
                    }
                  />
                </Stack>
              </Radio.Group>
            </Stack>
          </Paper>

          <Paper withBorder p="md">
            <Stack gap="sm">
              {sectionTitle(
                9,
                getTranslation("DEVIATION_SECTION_SUPERVISOR_COMMENTS") ||
                  "Supervisor comments",
              )}
              <Textarea
                minRows={3}
                {...form.getInputProps("supervisorComments")}
              />
            </Stack>
          </Paper>

          <Group justify="flex-end">
            {validationMessage && (
              <Text c="red" size="sm">
                {validationMessage}
              </Text>
            )}
            <Button variant="outline" onClick={onClose}>
              {getTranslation("DEVIATION_ACTION_CANCEL") || "Cancel"}
            </Button>
            <Button type="submit" loading={submitting}>
              {getTranslation("DEVIATION_ACTION_SAVE") || "Save"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
